/**
 * Wire schemas for Phase 2: workforce (engagements), attendance (append-only
 * events and the worker-days derived from them), payroll runs and the
 * worker's own record.
 *
 * Synced entities (`project_worker`, `attendance_event`, `worker_day`) are
 * parsed here before they reach IndexedDB. The API renders decimals as
 * strings ("8.00"), so hours and fractions are coerced to numbers. Money stays
 * `{amount_minor, currency}` and is never added up on the client for anything
 * shown as authoritative.
 */
import { z } from 'zod';

const id = z.string();
const nullableId = z.string().nullable().default(null);
const date = z.string().nullable().default(null);
const text = z.string().catch('').default('');
const decimal = z.coerce.number().catch(0).default(0);
const nullableDecimal = z.union([z.null(), z.coerce.number()]).catch(null).default(null);
const version = z.number().int().nullable().default(null);

export const moneySchema = z.object({ amount_minor: z.number().int(), currency: z.string() });

// ---------------------------------------------------------------------------
// Workforce (synced as project_worker)
// ---------------------------------------------------------------------------

export const ENGAGEMENT_STATUSES = ['active', 'suspended', 'ended'] as const;
export const RATE_BASES = ['daily', 'hourly', 'weekly', 'task'] as const;
export const PAYOUT_METHODS = ['mobile_money', 'bank', 'cash'] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

export const rateSchema = z.object({
  basis: z.string().default('daily'),
  amount_minor: z.number().int(),
  currency: z.string(),
  effective_from: date,
});
export type LabourRate = z.infer<typeof rateSchema>;

export const engagementSchema = z.object({
  id,
  project_id: id,
  worker: z
    .object({
      id: text,
      display_name: text,
      photo_url: z.string().nullable().catch(null).default(null),
      temporary: z.boolean().default(true),
      verification_status: z.string().default('unverified'),
    })
    .default({
      id: '',
      display_name: '',
      photo_url: null,
      temporary: true,
      verification_status: 'unverified',
    }),
  /**
   * The engaging (and paying) company. The main contractor sees its
   * subcontractors' workers on its roster read-only (`own: false`: no contact,
   * payout or pay); a subcontractor sees only its own. A record engaged on this
   * device is its own until the server says otherwise.
   */
  party_id: nullableId,
  company: text,
  own: z.boolean().catch(true).default(true),
  trade: z.string().nullable().default(null),
  role_title: text,
  supervisor_id: nullableId,
  stage_id: nullableId,
  start_date: date,
  end_date: date,
  status: z.string().default('active'),
  /** Only for the engaging company's people (workforce.view_personal / manage_own). */
  phone: z.string().nullable().optional(),
  payout_method: z.string().optional(),
  payout_destination_hint: z.string().optional(),
  /** Only from the REST list, and only with cost.view_internal (never synced). */
  current_rate: rateSchema.nullable().optional(),
  version,
});
export type Engagement = z.infer<typeof engagementSchema>;

/** Fields the server lets `workforce.manage` change (UpdateEngagement.EDITABLE). */
export const ENGAGEMENT_EDITABLE = ['role_title', 'status', 'end_date', 'trade_id'] as const;

export const tradeSchema = z.object({
  key: z.string(),
  name: z.string(),
  category: z.string().default('general'),
});
export type Trade = z.infer<typeof tradeSchema>;

// ---------------------------------------------------------------------------
// Attendance (synced as attendance_event and worker_day)
// ---------------------------------------------------------------------------

export const ATTENDANCE_KINDS = ['check_in', 'check_out'] as const;
export type AttendanceKind = (typeof ATTENDANCE_KINDS)[number];
export type AttendanceMethod = 'self' | 'supervisor' | 'bulk';

export const attendanceEventSchema = z.object({
  id,
  project_id: id,
  /** Empty on a correction recorded on this device (the server fills it in). */
  engagement_id: text,
  /** check_in | check_out | void */
  kind: z.string(),
  method: z.string().default('supervisor'),
  occurred_at: z.string(),
  work_date: date,
  voids_id: nullableId,
  reason: text,
  has_location: z.boolean().default(false),
  photo_asset_id: nullableId,
  recorded_by_id: nullableId,
  clock_suspect: z.boolean().default(false),
  created_at: date,
  /** Device-only: the replacement time a queued correction asked for. */
  replacement_occurred_at: z.string().nullable().optional(),
  version,
});
export type AttendanceEvent = z.infer<typeof attendanceEventSchema>;

export const DAY_FLAGS = [
  'missing_check_out',
  'check_out_without_check_in',
  'repeated_check_in',
  'clock_suspect',
  'no_rate',
] as const;
/** Flags that block approving the day until corrected or adjusted. */
export const BLOCKING_FLAGS = ['missing_check_out', 'check_out_without_check_in'];

export const workerDaySchema = z.object({
  id,
  project_id: id,
  engagement_id: id,
  work_date: z.string(),
  first_in: date,
  last_out: date,
  hours_worked: decimal,
  computed_fraction: decimal,
  adjusted_fraction: nullableDecimal,
  payable_fraction: decimal,
  overtime_candidate_hours: decimal,
  approved_overtime_hours: decimal,
  adjustment_reason: text,
  flags: z.array(z.string()).catch([]).default([]),
  /** open | approved */
  status: z.string().default('open'),
  approved_at: date,
  in_payroll: z.boolean().default(false),
  version,
});
export type WorkerDay = z.infer<typeof workerDaySchema>;

export const headcountSchema = z.object({
  from: z.string(),
  to: z.string(),
  days: z
    .array(
      z.object({
        work_date: z.string(),
        party_id: id,
        company: text,
        party_role: text,
        own: z.boolean().default(false),
        engaged: z.number().int().default(0),
        checked_in: z.number().int().default(0),
        on_site: z.number().int().default(0),
      }),
    )
    .default([]),
});
export type Headcount = z.infer<typeof headcountSchema>;

export const daySheetSchema = z.object({
  days: z.array(workerDaySchema).default([]),
  events: z.array(attendanceEventSchema).default([]),
});
export type DaySheet = z.infer<typeof daySheetSchema>;

// ---------------------------------------------------------------------------
// Payroll (online only)
// ---------------------------------------------------------------------------

export const RUN_STATUSES = [
  'draft',
  'submitted',
  'reviewed',
  'approved',
  'paying',
  'paid',
  'partially_paid',
  'cancelled',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

export const ADJUSTMENT_KINDS = ['allowance', 'deduction', 'advance_recovery'] as const;

export const linePaymentSchema = z.object({
  /** open | settling | settled | cancelled */
  obligation_status: z.string().nullable().default(null),
  /** initiated | pending | succeeded | failed | reversed */
  status: z.string().nullable().default(null),
  attempts: z.number().int().default(0),
  failure_reason: text,
  provider_reference: text,
});
export type LinePayment = z.infer<typeof linePaymentSchema>;

export const payrollLineSchema = z.object({
  id,
  engagement_id: id,
  worker_name: text,
  days: decimal,
  hours: decimal,
  overtime_hours: decimal,
  basis: text,
  base: moneySchema,
  overtime: moneySchema,
  allowances: moneySchema,
  deductions: moneySchema,
  net: moneySchema,
  /** Net is rounded to whole units for M-PESA; this is what the rounding changed. */
  rounding: moneySchema.optional(),
  flags: z.array(z.string()).catch([]).default([]),
  payment: linePaymentSchema.default({
    obligation_status: null,
    status: null,
    attempts: 0,
    failure_reason: '',
    provider_reference: '',
  }),
  payout_method: z.string().optional(),
  payout_destination_hint: z.string().optional(),
});
export type PayrollLine = z.infer<typeof payrollLineSchema>;

export const payrollAdjustmentSchema = z.object({
  id,
  engagement_id: id,
  kind: z.string(),
  amount: moneySchema,
  reason: text,
});
export type PayrollAdjustment = z.infer<typeof payrollAdjustmentSchema>;

export const payrollRunSchema = z.object({
  id,
  project_id: id,
  period_start: z.string(),
  period_end: z.string(),
  status: z.string(),
  worker_count: z.number().int().default(0),
  net: moneySchema,
  prepared_by_id: nullableId,
  reviewed_by_id: nullableId,
  approved_by_id: nullableId,
  prepared_by_name: z.string().nullable().default(null),
  reviewed_by_name: z.string().nullable().default(null),
  approved_by_name: z.string().nullable().default(null),
  note: z.string().default(''),
  submitted_at: date,
  reviewed_at: date,
  approved_at: date,
  /** The party that authorises and pays: contractor | owner | subcontractor (its own payroll). */
  payer: z.string().default('contractor'),
  /** Null: the project's payroll; a party id: a subcontractor paying its own workers. */
  party_id: nullableId,
  /** The paying company's name. */
  payer_company: text,
  /** Waiting on a second person — and, when nobody else on its side can take the step, why. */
  waiting: z
    .object({ step: z.string(), blocked: z.boolean().default(false), message: text })
    .nullable()
    .catch(null)
    .default(null),
  /** Approved pay against confirmed payouts and the payer's own ledger. */
  reconciliation: z
    .object({
      owed: moneySchema,
      paid: moneySchema,
      outstanding: moneySchema,
      ledger_payable: moneySchema,
      reconciled: z.boolean(),
    })
    .nullable()
    .optional(),
  version,
  // Present only for people who may see the breakdown (see the API's can_see_lines).
  gross: moneySchema.optional(),
  allowances: moneySchema.optional(),
  deductions: moneySchema.optional(),
  lines: z.array(payrollLineSchema).optional(),
  adjustments: z.array(payrollAdjustmentSchema).optional(),
});
export type PayrollRun = z.infer<typeof payrollRunSchema>;

// ---------------------------------------------------------------------------
// The worker's own record (GET /me/worker-profile)
// ---------------------------------------------------------------------------

export const myWorkSchema = z.object({
  profile: z
    .object({
      id,
      display_name: text,
      primary_trade: z.string().nullable().default(null),
      verification_status: z.string().default('unverified'),
    })
    .nullable()
    .default(null),
  engagements: z
    .array(
      z.object({
        id,
        project_id: id,
        project_name: text,
        trade: z.string().nullable().default(null),
        status: z.string().default('active'),
        start_date: date,
        payout_destination_hint: text,
        /** The project's owner side can see this worker's attendance and pay (labour evidence). */
        owner_can_see_pay: z.boolean().optional().catch(undefined),
      }),
    )
    .default([]),
  days: z
    .array(
      z.object({
        engagement_id: id,
        project_name: text,
        work_date: z.string(),
        hours_worked: decimal,
        payable_fraction: decimal,
        approved_overtime_hours: decimal,
        status: z.string().default('open'),
      }),
    )
    .default([]),
  earnings: z
    .array(
      z.object({
        id: z.string().default(''),
        project_name: text,
        /** Module 13: the payslip — the worker's own figures only. */
        payslip: z
          .object({
            employer: text,
            hours: decimal,
            overtime_hours: decimal,
            base_minor: z.number().default(0),
            overtime_minor: z.number().default(0),
            allowances_minor: z.number().default(0),
            deductions_minor: z.number().default(0),
            rounding_minor: z.number().default(0),
            adjustments: z
              .array(z.object({ kind: z.string(), amount_minor: z.number(), reason: text }))
              .default([]),
          })
          .nullable()
          .default(null),
        period_start: z.string(),
        period_end: z.string(),
        days: decimal,
        net: moneySchema,
        payment_status: z.string().nullable().default(null),
        /** Latest attempt: "failed" while payment_status is open again = being sorted out. */
        last_payment_status: z.string().nullable().default(null),
        paid_at: date,
      }),
    )
    .default([]),
});
export type MyWork = z.infer<typeof myWorkSchema>;
